//frontend/src/components/admin/NotificationTab.jsx
import { useState, useEffect } from 'react';
import { notificationAPI, companyAPI } from '../../services/api';

const NotificationTab = () => {
  // ============================================
  // STATE
  // ============================================
  const [activeTab, setActiveTab] = useState('registrations');
  
  // Бүртгэлийн хүсэлтүүд
  const [registrations, setRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regFilter, setRegFilter] = useState('pending');
  
  // Мэдэгдэл илгээх (шууд)
  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    target_type: 'all',
    type: 'announcement'
  });
  const [sending, setSending] = useState(false);
  const [targetCompanies, setTargetCompanies] = useState([]);
  const [targetShops, setTargetShops] = useState([]);
  const [targetDrivers, setTargetDrivers] = useState([]);
  const [allShops, setAllShops] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  
  // Хуваарьт мэдэгдэл
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    message: '',
    target_type: 'all',
    scheduled_date: '',
    scheduled_time: '',
    is_recurring: false,
    recurring_pattern: 'daily'
  });
  const [scheduling, setScheduling] = useState(false);
  
  // Invoice
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Invoice түүх
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null);
  
  // Registration Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // ============================================
  // FETCH REGISTRATIONS
  // ============================================
  const fetchRegistrations = async () => {
    setRegLoading(true);
    try {
      const response = await notificationAPI.getCompanyRegistrations({ 
        status: regFilter === 'all' ? undefined : regFilter 
      });
      if (response.data.success) {
        setRegistrations(response.data.data.registrations);
      }
    } catch (error) {
      console.error('Fetch registrations error:', error);
    } finally {
      setRegLoading(false);
    }
  };

  // ============================================
  // FETCH SCHEDULES
  // ============================================
  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const response = await notificationAPI.getSchedules();
      if (response.data.success) {
        setSchedules(response.data.data);
      }
    } catch (error) {
      console.error('Fetch schedules error:', error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  // ============================================
  // FETCH COMPANIES
  // ============================================
  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAll({ status: 'active' });
      if (response.data.success) {
        setCompanies(response.data.data.companies || response.data.data || []);
      }
    } catch (error) {
      console.error('Fetch companies error:', error);
    }
  };

  // ============================================
  // FETCH ALL SHOPS (for notification targeting)
  // ============================================
  const fetchAllShops = async () => {
    try {
      const { shopAPI } = await import('../../services/api');
      const response = await shopAPI.getAll({ status: 'active', limit: 500 });
      if (response.data.success) {
        setAllShops(response.data.data.shops || response.data.data || []);
      }
    } catch (error) {
      console.error('Fetch shops error:', error);
    }
  };

  // ============================================
  // FETCH ALL DRIVERS (for notification targeting)
  // ============================================
  const fetchAllDrivers = async () => {
    try {
      const { driverAPI } = await import('../../services/api');
      const response = await driverAPI.getAll({ status: 'active', limit: 500 });
      if (response.data.success) {
        setAllDrivers(response.data.data.drivers || response.data.data || []);
      }
    } catch (error) {
      console.error('Fetch drivers error:', error);
    }
  };

  // ============================================
  // FETCH INVOICE HISTORY
  // ============================================
  const fetchInvoiceHistory = async () => {
    setInvoiceHistoryLoading(true);
    try {
      const response = await notificationAPI.getInvoiceHistory({ limit: 50 });
      if (response.data.success) {
        setInvoiceHistory(response.data.data.invoices);
      }
    } catch (error) {
      console.error('Fetch invoice history error:', error);
    } finally {
      setInvoiceHistoryLoading(false);
    }
  };

  // ============================================
  // FETCH COMPANY FEE
  // ============================================
  const fetchCompanyFee = async (companyId, start, end) => {
    if (!companyId || !start || !end) return;
    
    setFeeLoading(true);
    setFeeData(null);
    try {
      const response = await notificationAPI.getCompanyFee(companyId, start, end);
      if (response.data.success) {
        setFeeData(response.data.data);
      }
    } catch (error) {
      console.error('Fetch company fee error:', error);
    } finally {
      setFeeLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (activeTab === 'registrations') {
      fetchRegistrations();
    } else if (activeTab === 'scheduled') {
      fetchSchedules();
    } else if (activeTab === 'invoice') {
      fetchCompanies();
      fetchInvoiceHistory();
    } else if (activeTab === 'send') {
      fetchCompanies();
      fetchAllShops();
      fetchAllDrivers();
    }
  }, [activeTab, regFilter]);

  // Компани эсвэл хугацаа сонгоход шимтгэл тооцоолох
  useEffect(() => {
    if (selectedCompany && periodStart && periodEnd) {
      fetchCompanyFee(selectedCompany, periodStart, periodEnd);
    }
  }, [selectedCompany, periodStart, periodEnd]);

  // ============================================
  // APPROVE REGISTRATION
  // ============================================
  const handleApprove = async () => {
    if (!selectedRegistration) return;
    
    setProcessing(true);
    try {
      const response = await notificationAPI.approveRegistration(selectedRegistration.id, {});
      if (response.data.success) {
        alert('Бүртгэл амжилттай зөвшөөрөгдлөө!');
        setShowDetailModal(false);
        setSelectedRegistration(null);
        fetchRegistrations();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setProcessing(false);
    }
  };

  // ============================================
  // REJECT REGISTRATION
  // ============================================
  const handleReject = async () => {
    if (!selectedRegistration) return;
    
    if (!rejectReason.trim()) {
      alert('Татгалзсан шалтгаан оруулна уу');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await notificationAPI.rejectRegistration(selectedRegistration.id, {
        rejection_reason: rejectReason
      });
      if (response.data.success) {
        alert('Бүртгэл татгалзагдлаа');
        setShowDetailModal(false);
        setSelectedRegistration(null);
        setRejectReason('');
        fetchRegistrations();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setProcessing(false);
    }
  };

  // ============================================
  // DELETE REGISTRATION
  // ============================================
  const handleDeleteRegistration = async (reg) => {
    if (!window.confirm(`"${reg.company_name}" бүртгэлийн хүсэлтийг устгах уу?`)) {
      return;
    }
    
    try {
      const response = await notificationAPI.deleteRegistration(reg.id);
      if (response.data.success) {
        fetchRegistrations();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // ============================================
  // SEND INSTANT NOTIFICATION
  // ============================================
  const handleSendInstant = async (e) => {
    e.preventDefault();
    
    if (!sendForm.title.trim() || !sendForm.message.trim()) {
      alert('Гарчиг болон мэдэгдэл оруулна уу');
      return;
    }

    // Тодорхой хүлээн авагч сонгосон эсэхийг шалгах
    let target_ids = [];
    if (sendForm.target_type === 'specific_company' && targetCompanies.length === 0) {
      alert('Компани сонгоно уу');
      return;
    }
    if (sendForm.target_type === 'specific_shop' && targetShops.length === 0) {
      alert('Шоп сонгоно уу');
      return;
    }
    if (sendForm.target_type === 'specific_driver' && targetDrivers.length === 0) {
      alert('Жолооч сонгоно уу');
      return;
    }

    if (sendForm.target_type === 'specific_company') {
      target_ids = targetCompanies.map(id => parseInt(id));
    } else if (sendForm.target_type === 'specific_shop') {
      target_ids = targetShops.map(id => parseInt(id));
    } else if (sendForm.target_type === 'specific_driver') {
      target_ids = targetDrivers.map(id => parseInt(id));
    }
    
    setSending(true);
    try {
      const response = await notificationAPI.sendNotification({
        ...sendForm,
        target_ids: target_ids.length > 0 ? target_ids : undefined
      });
      if (response.data.success) {
        alert(`Мэдэгдэл илгээгдлээ! (${response.data.data.sent} хэрэглэгч)`);
        setSendForm({
          title: '',
          message: '',
          target_type: 'all',
          type: 'announcement'
        });
        setTargetCompanies([]);
        setTargetShops([]);
        setTargetDrivers([]);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSending(false);
    }
  };

  // ============================================
  // CREATE SCHEDULE
  // ============================================
  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    
    if (!scheduleForm.title.trim() || !scheduleForm.message.trim()) {
      alert('Гарчиг болон мэдэгдэл оруулна уу');
      return;
    }
    
    if (!scheduleForm.scheduled_date || !scheduleForm.scheduled_time) {
      alert('Огноо болон цаг сонгоно уу');
      return;
    }
    
    const scheduledAt = `${scheduleForm.scheduled_date}T${scheduleForm.scheduled_time}:00`;
    
    setScheduling(true);
    try {
      const response = await notificationAPI.sendNotification({
        title: scheduleForm.title,
        message: scheduleForm.message,
        target_type: scheduleForm.target_type,
        type: 'announcement',
        schedule_at: scheduledAt,
        is_recurring: scheduleForm.is_recurring,
        recurring_pattern: scheduleForm.is_recurring ? scheduleForm.recurring_pattern : null
      });
      
      if (response.data.success) {
        alert('Хуваарьт мэдэгдэл үүсгэгдлээ!');
        setScheduleForm({
          title: '',
          message: '',
          target_type: 'all',
          scheduled_date: '',
          scheduled_time: '',
          is_recurring: false,
          recurring_pattern: 'daily'
        });
        setShowScheduleForm(false);
        fetchSchedules();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setScheduling(false);
    }
  };

  // ============================================
  // CANCEL SCHEDULE
  // ============================================
  const handleCancelSchedule = async (schedule) => {
    if (!window.confirm('Энэ хуваарьт мэдэгдлийг цуцлах уу?')) {
      return;
    }
    
    try {
      const response = await notificationAPI.cancelSchedule(schedule.id);
      if (response.data.success) {
        fetchSchedules();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  // ============================================
  // SEND INVOICE
  // ============================================
  const handleSendInvoice = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany || !feeData) {
      alert('Компани сонгоод шимтгэл тооцоологдсон байх ёстой');
      return;
    }
    
    setSendingInvoice(true);
    try {
      const response = await notificationAPI.sendInvoice({
        company_id: parseInt(selectedCompany),
        period_start: periodStart,
        period_end: periodEnd,
        delivery_count: feeData.all_delivery_count,
        fee_per_delivery: feeData.fee_per_delivery,
        total_fee: feeData.all_time_total_fee,
        remaining_balance: feeData.remaining_balance
      });
      
      if (response.data.success) {
        alert('Нэхэмжлэх амжилттай илгээгдлээ!');
        setSelectedCompany('');
        setPeriodStart('');
        setPeriodEnd('');
        setFeeData(null);
        fetchInvoiceHistory();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSendingInvoice(false);
    }
  };

  // ============================================
  // DELETE INVOICE HISTORY
  // ============================================
  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm('Энэ түүхийг устгах уу?')) {
      return;
    }
    
    try {
      const response = await notificationAPI.deleteInvoiceHistory(invoice.id);
      if (response.data.success) {
        fetchInvoiceHistory();
        setShowInvoiceDetail(null);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('mn-MN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    return new Date(dateString).toLocaleDateString('mn-MN');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Хүлээгдэж буй</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Зөвшөөрсөн</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Татгалзсан</span>;
      case 'sent':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Илгээсэн</span>;
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Идэвхтэй</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">Цуцлагдсан</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const getTargetLabel = (targetType) => {
    switch (targetType) {
      case 'all': return '🌐 Бүгд';
      case 'companies': return '🏢 Компаниуд';
      case 'shops': return '🛍️ Шопууд';
      case 'drivers': return '🚚 Жолоочид';
      default: return targetType;
    }
  };

  const getRecurringLabel = (pattern) => {
    switch (pattern) {
      case 'daily': return 'Өдөр бүр';
      case 'weekly': return '7 хоног бүр';
      case 'monthly': return 'Сар бүр';
      default: return pattern;
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-6">
      {/* Main Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        <button
          onClick={() => setActiveTab('registrations')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            activeTab === 'registrations'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          🏢 Бүртгэлийн хүсэлтүүд
        </button>
        <button
          onClick={() => setActiveTab('send')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            activeTab === 'send'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📤 Мэдэгдэл илгээх
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            activeTab === 'scheduled'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          ⏰ Хуваарьт мэдэгдэл
        </button>
        <button
          onClick={() => setActiveTab('invoice')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            activeTab === 'invoice'
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📄 Invoice илгээх
        </button>
      </div>

      {/* ============================================
          REGISTRATIONS TAB
          ============================================ */}
      {activeTab === 'registrations' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex flex-wrap gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(filter => (
              <button
                key={filter}
                onClick={() => setRegFilter(filter)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  regFilter === filter
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter === 'pending' && '⏳ Хүлээгдэж буй'}
                {filter === 'approved' && '✅ Зөвшөөрсөн'}
                {filter === 'rejected' && '❌ Татгалзсан'}
                {filter === 'all' && '📋 Бүгд'}
              </button>
            ))}
          </div>

          {/* Table */}
          {regLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Уншиж байна...</p>
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-5xl">📭</span>
              <p className="mt-4 text-gray-500">Хүсэлт байхгүй байна</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Компани</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">И-мэйл</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Утас</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Огноо</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Төлөв</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm text-right">Үйлдэл</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map(reg => (
                    <tr key={reg.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {reg.logo_thumbnail ? (
                            <img 
                              src={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}${reg.logo_thumbnail}`}
                              alt="" 
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                              🏢
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{reg.company_name}</p>
                            <p className="text-xs text-gray-500">РД: {reg.register_number || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{reg.email}</td>
                      <td className="py-3 px-4 text-gray-700">{reg.phone}</td>
                      <td className="py-3 px-4 text-gray-500 text-sm">{formatDate(reg.created_at)}</td>
                      <td className="py-3 px-4">{getStatusBadge(reg.status)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedRegistration(reg);
                              setShowDetailModal(true);
                            }}
                            className="px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-100 transition"
                          >
                            Дэлгэрэнгүй
                          </button>
                          <button
                            onClick={() => handleDeleteRegistration(reg)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition"
                            title="Устгах"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================
          SEND NOTIFICATION TAB
          ============================================ */}
      {activeTab === 'send' && (
        <form onSubmit={handleSendInstant} className="max-w-2xl space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              📢 <strong>Шууд илгээх</strong> - Мэдэгдэл тэр даруй бүх хүлээн авагчид илгээгдэнэ
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Хүлээн авагч</label>
            <select
              value={sendForm.target_type}
              onChange={(e) => {
                setSendForm({ ...sendForm, target_type: e.target.value });
                setTargetCompanies([]);
                setTargetShops([]);
                setTargetDrivers([]);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">🌐 Бүх хэрэглэгч</option>
              <option value="companies">🏢 Бүх компаниуд</option>
              <option value="specific_company">🏢 Тодорхой компани(ууд)</option>
              <option value="shops">🛍️ Бүх онлайн шопууд</option>
              <option value="specific_shop">🛍️ Тодорхой шоп(ууд)</option>
              <option value="drivers">🚚 Бүх жолоочид</option>
              <option value="specific_driver">🚚 Тодорхой жолооч(ууд)</option>
            </select>
          </div>

          {/* Тодорхой компани сонгох */}
          {sendForm.target_type === 'specific_company' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Компани сонгох <span className="text-red-500">*</span>
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {companies.map(company => (
                  <label key={company.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={targetCompanies.includes(company.id.toString())}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTargetCompanies([...targetCompanies, company.id.toString()]);
                        } else {
                          setTargetCompanies(targetCompanies.filter(id => id !== company.id.toString()));
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm">{company.company_name}</span>
                  </label>
                ))}
              </div>
              {targetCompanies.length > 0 && (
                <p className="text-xs text-primary-600 mt-1">{targetCompanies.length} компани сонгогдсон</p>
              )}
            </div>
          )}

          {/* Тодорхой шоп сонгох */}
          {sendForm.target_type === 'specific_shop' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Шоп сонгох <span className="text-red-500">*</span>
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {allShops.map(shop => (
                  <label key={shop.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={targetShops.includes(shop.id.toString())}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTargetShops([...targetShops, shop.id.toString()]);
                        } else {
                          setTargetShops(targetShops.filter(id => id !== shop.id.toString()));
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm">{shop.shop_name}</span>
                  </label>
                ))}
              </div>
              {targetShops.length > 0 && (
                <p className="text-xs text-primary-600 mt-1">{targetShops.length} шоп сонгогдсон</p>
              )}
            </div>
          )}

          {/* Тодорхой жолооч сонгох */}
          {sendForm.target_type === 'specific_driver' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Жолооч сонгох <span className="text-red-500">*</span>
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                {allDrivers.map(driver => (
                  <label key={driver.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={targetDrivers.includes(driver.id.toString())}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTargetDrivers([...targetDrivers, driver.id.toString()]);
                        } else {
                          setTargetDrivers(targetDrivers.filter(id => id !== driver.id.toString()));
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm">{driver.driver_name} ({driver.phone})</span>
                  </label>
                ))}
              </div>
              {targetDrivers.length > 0 && (
                <p className="text-xs text-primary-600 mt-1">{targetDrivers.length} жолооч сонгогдсон</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Гарчиг <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={sendForm.title}
              onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
              placeholder="Мэдэгдлийн гарчиг"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Мэдэгдэл <span className="text-red-500">*</span>
            </label>
            <textarea
              value={sendForm.message}
              onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
              placeholder="Мэдэгдлийн агуулга..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-50"
          >
            {sending ? '⏳ Илгээж байна...' : '📤 Мэдэгдэл илгээх'}
          </button>
        </form>
      )}

      {/* ============================================
          SCHEDULED NOTIFICATIONS TAB
          ============================================ */}
      {activeTab === 'scheduled' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Хуваарьт мэдэгдлүүд</h3>
            <button
              onClick={() => setShowScheduleForm(!showScheduleForm)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition"
            >
              {showScheduleForm ? '✕ Хаах' : '➕ Шинэ хуваарь'}
            </button>
          </div>

          {showScheduleForm && (
            <form onSubmit={handleCreateSchedule} className="p-4 bg-gray-50 rounded-xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Хүлээн авагч</label>
                  <select
                    value={scheduleForm.target_type}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, target_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">🌐 Бүх хэрэглэгч</option>
                    <option value="companies">🏢 Бүх компаниуд</option>
                    <option value="shops">🛍️ Бүх онлайн шопууд</option>
                    <option value="drivers">🚚 Бүх жолоочид</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Гарчиг <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={scheduleForm.title}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Мэдэгдэл <span className="text-red-500">*</span></label>
                <textarea
                  value={scheduleForm.message}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Огноо <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={scheduleForm.scheduled_date}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Цаг <span className="text-red-500">*</span></label>
                  <input
                    type="time"
                    value={scheduleForm.scheduled_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>

              <div className="p-3 bg-white border border-gray-200 rounded-lg space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_recurring}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, is_recurring: e.target.checked })}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <span className="font-medium text-gray-700">Давтамжтай илгээх</span>
                </label>
                {scheduleForm.is_recurring && (
                  <select
                    value={scheduleForm.recurring_pattern}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, recurring_pattern: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="daily">📅 Өдөр бүр</option>
                    <option value="weekly">📆 7 хоног бүр</option>
                    <option value="monthly">🗓️ Сар бүр</option>
                  </select>
                )}
              </div>

              <button
                type="submit"
                disabled={scheduling}
                className="w-full py-3 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition disabled:opacity-50"
              >
                {scheduling ? '⏳ Үүсгэж байна...' : '⏰ Хуваарь үүсгэх'}
              </button>
            </form>
          )}

          {schedulesLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <span className="text-5xl">⏰</span>
              <p className="mt-4 text-gray-500">Хуваарьт мэдэгдэл байхгүй</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Гарчиг</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Хүлээн авагч</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Хуваарь</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Давтамж</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm">Төлөв</th>
                    <th className="py-3 px-4 font-medium text-gray-600 text-sm text-right">Үйлдэл</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(schedule => (
                    <tr key={schedule.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{schedule.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{schedule.message}</p>
                      </td>
                      <td className="py-3 px-4">{getTargetLabel(schedule.target_type)}</td>
                      <td className="py-3 px-4 text-sm">{formatDate(schedule.scheduled_at)}</td>
                      <td className="py-3 px-4">
                        {schedule.is_recurring ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                            🔄 {getRecurringLabel(schedule.recurring_pattern)}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">Нэг удаа</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(schedule.status)}</td>
                      <td className="py-3 px-4 text-right">
                        {(schedule.status === 'pending' || schedule.status === 'active') && (
                          <button
                            onClick={() => handleCancelSchedule(schedule)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100"
                          >
                            Цуцлах
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================
          INVOICE TAB
          ============================================ */}
      {activeTab === 'invoice' && (
        <div className="space-y-6">
          {/* Invoice Form */}
          <form onSubmit={handleSendInvoice} className="p-4 bg-gray-50 rounded-xl space-y-4">
            <h3 className="font-semibold text-gray-900">📄 Шинэ Invoice илгээх</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Компани <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">-- Сонгох --</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.company_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Эхлэх огноо <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дуусах огноо <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            </div>

            {feeLoading && (
              <div className="p-4 bg-white rounded-lg text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Тооцоолж байна...</p>
              </div>
            )}

            {feeData && !feeLoading && (
            <div className="p-4 bg-white border-2 border-green-200 rounded-xl space-y-4">
              {/* Сонгосон хугацааны мэдээлэл */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500">Хугацаа</p>
                  <p className="font-medium text-sm">{feeData.period_start} - {feeData.period_end}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Хугацааны хүргэлт</p>
                  <p className="font-bold text-lg">{feeData.delivery_count.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Нэгж шимтгэл</p>
                  <p className="font-medium">{feeData.fee_per_delivery.toLocaleString()}₮</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Хугацааны шимтгэл</p>
                  <p className="font-bold text-lg">{feeData.total_fee.toLocaleString()}₮</p>
                </div>
              </div>
              
              {/* Нийт тооцоо */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Нийт хүргэлт</p>
                    <p className="font-medium">{feeData.all_delivery_count?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Нийт шимтгэл</p>
                    <p className="font-medium">{feeData.all_time_total_fee?.toLocaleString() || 0}₮</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Төлсөн</p>
                    <p className="font-medium text-blue-600">{feeData.total_paid?.toLocaleString() || 0}₮</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2">
                    <p className="text-xs text-orange-600 font-medium">Төлөх үлдэгдэл</p>
                    <p className="font-bold text-xl text-orange-600">{feeData.remaining_balance?.toLocaleString() || 0}₮</p>
                  </div>
                </div>
              </div>
            </div>
          )}

            <button
              type="submit"
              disabled={sendingInvoice || !feeData}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50"
            >
              {sendingInvoice ? '⏳ Илгээж байна...' : '📄 Нэхэмжлэх илгээх'}
            </button>
          </form>

          {/* Invoice History */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">📋 Илгээсэн Invoice түүх</h3>
            
            {invoiceHistoryLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : invoiceHistory.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <span className="text-4xl">📭</span>
                <p className="mt-2 text-gray-500">Түүх байхгүй</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 text-left">
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm">Компани</th>
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm">Хугацаа</th>
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm">Хүргэлт</th>
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm">Нийт дүн</th>
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm">Илгээсэн</th>
                      <th className="py-3 px-4 font-medium text-gray-600 text-sm text-right">Үйлдэл</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceHistory.map(invoice => (
                      <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-900">{invoice.company?.company_name || '-'}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDateOnly(invoice.period_start)} - {formatDateOnly(invoice.period_end)}
                        </td>
                        <td className="py-3 px-4 font-medium">{invoice.delivery_count}</td>
                        <td className="py-3 px-4 font-bold text-green-600">
                          {parseFloat(invoice.total_fee).toLocaleString()}₮
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-gray-600">{invoice.sent_by_name || '-'}</p>
                          <p className="text-xs text-gray-400">{formatDate(invoice.created_at)}</p>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setShowInvoiceDetail(invoice)}
                              className="px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-100"
                            >
                              Дэлгэрэнгүй
                            </button>
                            <button
                              onClick={() => handleDeleteInvoice(invoice)}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================
          REGISTRATION DETAIL MODAL
          ============================================ */}
      {showDetailModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Бүртгэлийн хүсэлт</h3>
              <button
                onClick={() => { setShowDetailModal(false); setSelectedRegistration(null); setRejectReason(''); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-center">
                {selectedRegistration.logo_url ? (
                  <img src={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}${selectedRegistration.logo_url}`} alt="" className="w-24 h-24 rounded-xl object-cover border" />
                ) : (
                  <div className="w-24 h-24 bg-gray-200 rounded-xl flex items-center justify-center text-4xl">🏢</div>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Компани</span><span className="font-medium">{selectedRegistration.company_name}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">РД</span><span className="font-medium">{selectedRegistration.register_number || '-'}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">И-мэйл</span><span className="font-medium">{selectedRegistration.email}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Утас</span><span className="font-medium">{selectedRegistration.phone}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Хаяг</span><span className="font-medium">{selectedRegistration.address || '-'}</span></div>
                <div className="flex justify-between py-2"><span className="text-gray-500">Төлөв</span>{getStatusBadge(selectedRegistration.status)}</div>
              </div>
              {selectedRegistration.status === 'rejected' && selectedRegistration.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Татгалзсан шалтгаан:</p>
                  <p className="text-sm text-red-600">{selectedRegistration.rejection_reason}</p>
                </div>
              )}
              {selectedRegistration.status === 'pending' && (
                <div className="space-y-3 pt-4">
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Татгалзах шалтгаан..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="flex gap-3">
                    <button onClick={handleReject} disabled={processing} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50">
                      {processing ? '...' : '❌ Татгалзах'}
                    </button>
                    <button onClick={handleApprove} disabled={processing} className="flex-1 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50">
                      {processing ? '...' : '✅ Зөвшөөрөх'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          INVOICE DETAIL MODAL
          ============================================ */}
      {showInvoiceDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">📄 Invoice дэлгэрэнгүй</h3>
              <button onClick={() => setShowInvoiceDetail(null)} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">Компани</p>
                <p className="font-bold text-lg">{showInvoiceDetail.company?.company_name}</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Хугацаа</span><span className="font-medium">{formatDateOnly(showInvoiceDetail.period_start)} - {formatDateOnly(showInvoiceDetail.period_end)}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Хүргэлтийн тоо</span><span className="font-medium">{showInvoiceDetail.delivery_count}</span></div>
                <div className="flex justify-between py-2 border-b"><span className="text-gray-500">Нэгж шимтгэл</span><span className="font-medium">{parseFloat(showInvoiceDetail.fee_per_delivery).toLocaleString()}₮</span></div>
                <div className="flex justify-between py-3 bg-green-50 rounded-lg px-3">
                  <span className="font-semibold">Нийт дүн</span>
                  <span className="font-bold text-green-600 text-lg">{parseFloat(showInvoiceDetail.total_fee).toLocaleString()}₮</span>
                </div>
                <div className="flex justify-between py-2 border-t mt-2"><span className="text-gray-500">Илгээсэн</span><span className="font-medium">{showInvoiceDetail.sent_by_name}</span></div>
                <div className="flex justify-between py-2"><span className="text-gray-500">Огноо</span><span className="font-medium">{formatDate(showInvoiceDetail.created_at)}</span></div>
              </div>
              <button
                onClick={() => handleDeleteInvoice(showInvoiceDetail)}
                className="w-full py-2.5 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100"
              >
                🗑️ Устгах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationTab;